import { useRef, useState, useMemo, useEffect } from "react";
import CompanyService, {
  DataType,
  Entities
} from "../../../services/CompanyService";
import { TranslationService } from "../../../services/TranslationService";
import {
  AutocompleteClient,
  NumberInput
} from "../../shared/components/Editors";
import Dropdown from "../../shared/components/Dropdown";
import { CompanyAuth } from "../../../entities/login/CompanyAuth";
import { ENTITY_KEY } from "../../../services/FilterService";

type FilterRow = {
  id: number;
  fieldId: string;
  operator: string;
  value: string;
};

type FieldKind = "select" | "number" | "text" | "client";

type FieldDef = {
  fieldId: string;
  label: string;
  kind: FieldKind;
  supportsNegate: boolean;
  group: string;
  items?: { label: string; value: string }[];
};

const OPERATOR_EQUAL = "Equal";
const OPERATOR_ISNOT = "IsNot";
const OPERATOR_GREATER = "MoreThan";
const OPERATOR_LESS = "LessThan";

const OPERATOR_SYMBOLS: Record<string, string> = {
  [OPERATOR_ISNOT]: "<>",
  [OPERATOR_GREATER]: ">",
  [OPERATOR_LESS]: "<"
};

// "-10" now maps to the real Client "AmountDue" field (FilterService.ts), so the
// synthetic Vencimiento/DueDate selector moved off it to avoid colliding.
const DUE_DATE_PSEUDO_FIELD_ID = "-39";

function parseFilter(str: string): FilterRow[] {
  if (!str) return [];
  return str
    .split("&")
    .filter(Boolean)
    .map((part, i) => {
      const negIdx = part.indexOf("<>");
      if (negIdx !== -1) {
        return {
          id: i,
          fieldId: part.slice(0, negIdx),
          operator: OPERATOR_ISNOT,
          value: part.slice(negIdx + 2)
        };
      }
      const gtIdx = part.indexOf(">");
      if (gtIdx !== -1) {
        return {
          id: i,
          fieldId: part.slice(0, gtIdx),
          operator: OPERATOR_GREATER,
          value: part.slice(gtIdx + 1)
        };
      }
      const ltIdx = part.indexOf("<");
      if (ltIdx !== -1) {
        return {
          id: i,
          fieldId: part.slice(0, ltIdx),
          operator: OPERATOR_LESS,
          value: part.slice(ltIdx + 1)
        };
      }
      const eqIdx = part.indexOf("=");
      if (eqIdx === -1)
        return { id: i, fieldId: part, operator: OPERATOR_EQUAL, value: "" };
      return {
        id: i,
        fieldId: part.slice(0, eqIdx),
        operator: OPERATOR_EQUAL,
        value: part.slice(eqIdx + 1)
      };
    });
}

function serializeFilter(rows: FilterRow[]): string {
  return rows
    .filter((r) => r.fieldId && r.value)
    .map((r) => `${r.fieldId}${OPERATOR_SYMBOLS[r.operator] ?? "="}${r.value}`)
    .join("&");
}

const DashboardFilterEditor = ({
  value,
  onChange,
  onHasIncomplete
}: {
  value: string;
  onChange: (filter: string) => void;
  onHasIncomplete?: (has: boolean) => void;
}) => {
  const { translate } = TranslationService;
  const idSeed = useRef(0);

  const [rows, setRows] = useState<FilterRow[]>(() => {
    const parsed = parseFilter(value);
    idSeed.current = parsed.length;
    return parsed;
  });

  const clientGroup = translate.FilterData.replace(
    ENTITY_KEY,
    translate.Client
  );
  const invoiceGroup = translate.FilterData.replace(
    ENTITY_KEY,
    translate.Invoice
  );

  const fieldDefs = useMemo((): FieldDef[] => {
    const agingItems = CompanyService.GetAgeingValues().map((x, i) => ({
      label: x.name,
      value: String(i)
    }));

    const segmentItems = CompanyService.getSegments().map((x) => ({
      label: x.Value,
      value: String(x.Id)
    }));

    const clientBuiltIn: FieldDef[] = [
      {
        // Synthetic dashboard-only pseudo-field (not a real FilterService field) —
        // uses an ID outside FilterService's Client (-1..-26) and Invoice (-1007..-1024)
        // ranges so it never collides with a real field again.
        fieldId: DUE_DATE_PSEUDO_FIELD_ID,
        label: translate.DueDate,
        kind: "select",
        supportsNegate: false,
        group: clientGroup,
        items: [
          { label: translate.Overdue, value: "1" },
          { label: translate.NoDue, value: "0" }
        ]
      },
      {
        fieldId: "-8",
        label: translate.Aging,
        kind: "select",
        supportsNegate: false,
        group: clientGroup,
        items: agingItems
      },
      {
        fieldId: "-7",
        label: translate.Segment,
        kind: "select",
        supportsNegate: false,
        group: clientGroup,
        items: segmentItems
      },
      {
        fieldId: "-9",
        label: translate.Amount,
        kind: "number",
        supportsNegate: true,
        group: clientGroup
      },
      {
        fieldId: "-10",
        label: translate.AmountDue,
        kind: "number",
        supportsNegate: true,
        group: clientGroup
      },
      {
        fieldId: "-37",
        label: translate.Client,
        kind: "client",
        supportsNegate: false,
        group: clientGroup
      }
    ];

    const invoiceBuiltIn: FieldDef[] = [
      {
        fieldId: "-1015",
        label: translate.IOStatus,
        kind: "select",
        supportsNegate: true,
        group: invoiceGroup,
        items: CompanyService.getIoStatus().map((x) => ({
          label: x.Value,
          value: String(x.IOStatusID)
        }))
      },
      {
        fieldId: "-1011",
        label: translate.Amount,
        kind: "number",
        supportsNegate: true,
        group: invoiceGroup
      },
      {
        fieldId: "-1012",
        label: translate.PendingAmount,
        kind: "number",
        supportsNegate: true,
        group: invoiceGroup
      }
    ];

    const additionals: CompanyAuth.AdditionalDefinition[] =
      CompanyService.getAdditionalDefinitions().filter(
        (x) => x.Entity === Entities.Client || x.Entity === Entities.Invoice
      );

    const additionalDefs: FieldDef[] = additionals.map((a) => {
      const isList = a.Type === 1;
      const isNumeric =
        a.Type === DataType.Number || a.Type === DataType.Currency;
      return {
        fieldId: String(a.AdditionalDefinitionID),
        label: a.Name,
        kind: isList ? "select" : isNumeric ? "number" : "text",
        supportsNegate: true,
        group: a.Entity === Entities.Client ? clientGroup : invoiceGroup,
        items: isList
          ? [
              { label: translate.Empty, value: "empty" },
              ...a.AdditionalDefinitionItems.map((item) => ({
                label: item.Value,
                value: String(item.AdditionalDefinitionItemID)
              }))
            ]
          : undefined
      };
    });

    return [...clientBuiltIn, ...invoiceBuiltIn, ...additionalDefs];
  }, [
    translate.DueDate,
    translate.Overdue,
    translate.NoDue,
    translate.Aging,
    translate.Segment,
    translate.IOStatus,
    translate.Amount,
    translate.AmountDue,
    translate.PendingAmount,
    translate.Client,
    translate.Empty,
    clientGroup,
    invoiceGroup
  ]);

  const fieldDropdownItems = useMemo(() => {
    const groups = new Map<string, FieldDef[]>();
    fieldDefs.forEach((f) => {
      if (!groups.has(f.group)) groups.set(f.group, []);
      groups.get(f.group)!.push(f);
    });
    return Array.from(groups.entries()).flatMap(([group, defs]) => [
      { text: group, isTitle: true as const, key: `group-${group}` },
      ...defs.map((f) => ({ text: f.label, value: f.fieldId, key: f.fieldId }))
    ]);
  }, [fieldDefs]);

  const basicOperatorItems = useMemo(
    () => [
      {
        text: TranslationService.getTranslation(OPERATOR_EQUAL),
        value: OPERATOR_EQUAL
      },
      {
        text: TranslationService.getTranslation(OPERATOR_ISNOT),
        value: OPERATOR_ISNOT
      }
    ],
    []
  );

  const numericOperatorItems = useMemo(
    () => [
      ...basicOperatorItems,
      {
        text: TranslationService.getTranslation(OPERATOR_GREATER),
        value: OPERATOR_GREATER
      },
      {
        text: TranslationService.getTranslation(OPERATOR_LESS),
        value: OPERATOR_LESS
      }
    ],
    [basicOperatorItems]
  );

  useEffect(() => {
    onHasIncomplete?.(rows.some((r) => r.fieldId && !r.value));
  }, [rows, onHasIncomplete]);

  const updateRows = (next: FilterRow[]) => {
    setRows(next);
    onChange(serializeFilter(next));
  };

  const makeRow = (): FilterRow => ({
    id: idSeed.current++,
    fieldId: "",
    operator: OPERATOR_EQUAL,
    value: ""
  });

  const addRow = () => updateRows([...rows, makeRow()]);

  const removeRow = (id: number) => updateRows(rows.filter((r) => r.id !== id));

  const patchRow = (id: number, patch: Partial<FilterRow>) =>
    updateRows(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const handleFieldChange = (id: number, fieldId: string | undefined) => {
    patchRow(id, {
      fieldId: fieldId ?? "",
      value: "",
      operator: OPERATOR_EQUAL
    });
  };

  return (
    <div className={rows.length > 0 ? "filters-container pb-2" : ""}>
      {rows.map((row) => {
        const def = fieldDefs.find((f) => f.fieldId === row.fieldId);
        return (
          <div key={row.id} className="filter-row">
            <div
              className="d-flex gap-2 align-items-center ms-0 me-1"
              style={{ flex: 1 }}
            >
              <div style={{ flex: "0 0 33%" }}>
                <Dropdown<string>
                  value={row.fieldId || undefined}
                  optionLabel={translate.Select}
                  onChange={(v) => handleFieldChange(row.id, v)}
                  items={fieldDropdownItems}
                />
              </div>

              {def?.supportsNegate && (
                <div
                  style={{
                    flex: def.kind === "number" ? "0 0 160px" : "0 0 110px"
                  }}
                >
                  <Dropdown<string>
                    value={row.operator}
                    onChange={(v) =>
                      patchRow(row.id, { operator: v ?? OPERATOR_EQUAL })
                    }
                    items={
                      def.kind === "number"
                        ? numericOperatorItems
                        : basicOperatorItems
                    }
                  />
                </div>
              )}

              <div style={{ flex: 1, minWidth: 0 }}>
                <ValueInput
                  def={def}
                  value={row.value}
                  onChange={(v) => patchRow(row.id, { value: v })}
                />
              </div>
            </div>

            <div className="delete">
              <button
                type="button"
                className="btn button-outline-primary"
                onClick={() => removeRow(row.id)}
              >
                <i className="fa-light fa-times text-danger fa-lg" />
              </button>
            </div>
          </div>
        );
      })}

      <div className="addRow align-content-center mt-1">
        <button type="button" className="btn btn-link p-0" onClick={addRow}>
          <i className="fa-light fa-plus me-2" />
          {translate.Add}
        </button>
      </div>
    </div>
  );
};

const ValueInput = ({
  def,
  value,
  onChange
}: {
  def: FieldDef | undefined;
  value: string;
  onChange: (v: string) => void;
}) => {
  const { translate } = TranslationService;

  if (!def) {
    return <Dropdown onChange={() => undefined} items={[]} />;
  }

  if (def.kind === "client") {
    return <AutocompleteClient onChange={onChange} defaultValue={value} />;
  }

  if (def.kind === "number") {
    return (
      <NumberInput
        allowDecimal
        allowNegative
        className="form-control"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (def.kind === "text") {
    return (
      <input
        type="text"
        className="form-control"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  return (
    <Dropdown<string>
      value={value || undefined}
      optionLabel={translate.Select}
      onChange={(v) => onChange(v ?? "")}
      items={(def.items ?? []).map((item) => ({
        text: item.label,
        value: item.value
      }))}
    />
  );
};

export default DashboardFilterEditor;
